import { ILoopingKeyframeStop, ITransformItem } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React from "react";
import KuikaSlider from "../../../ui-kit/kuika-slider/kuika-slider";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { defaultKeyframeStop } from "./looping-animation-helpers";
import TransformAccordion from "./transform-accordion/transform-accordion";

const OFFSET_MIN: number = 0;
const OFFSET_MAX: number = 100;
const MIN_STOPS: number = 2;

export interface KeyframeStopEditorProps {
  stops: ILoopingKeyframeStop[];
  /** Called with the full next stop array on any edit. Sets the owning animation to a custom timeline. */
  onChange: (next: ILoopingKeyframeStop[]) => void;
}

const stopSummary = (stop: ILoopingKeyframeStop): string => {
  const bits: string[] = [];
  const count: number = stop.transforms?.length ?? 0;
  if (count > 0) bits.push(count === 1 ? "1 transform" : `${count} transforms`);
  if (stop.opacity !== undefined) bits.push(`opacity ${stop.opacity}`);
  return bits.length > 0 ? bits.join(" · ") : "Empty";
};

/**
 * Custom keyframe-stop editor. Mirrors `TransformAccordion`'s structure exactly
 * (uppercase header + add button, one card per stop, click to expand an inline
 * form, blue-tint active state) so it reads as a sibling of the other Effects
 * editors rather than a bespoke widget. Only one stop's form is open at a time,
 * so the popover never grows into a tall stack. Offsets are clamped but NOT
 * reordered while editing (CSS reads @keyframes by offset regardless of source
 * order). Filter-per-stop authoring is preset-driven for now — the model
 * supports it; this manual editor is transform+opacity in v1.
 */
const KeyframeStopEditor: React.FC<KeyframeStopEditorProps> = ({ stops, onChange }) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(0);

  const patchStopAt = (index: number, patch: Partial<ILoopingKeyframeStop>): void => {
    onChange(
      stops.map((s: ILoopingKeyframeStop, i: number): ILoopingKeyframeStop => (i === index ? { ...s, ...patch } : s))
    );
  };

  const removeStopAt = (index: number): void => {
    onChange(stops.filter((_: ILoopingKeyframeStop, i: number): boolean => i !== index));
    setActiveIndex((prev: number | null): number | null => {
      if (prev === null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const addStop = (): void => {
    const maxOffset: number = stops.reduce((m: number, s: ILoopingKeyframeStop): number => Math.max(m, s.offset), 0);
    const offset: number = maxOffset >= OFFSET_MAX ? OFFSET_MAX : Math.min(OFFSET_MAX, maxOffset + 25);
    onChange([...stops, defaultKeyframeStop(offset)]);
    setActiveIndex(stops.length);
  };

  const toggle = (index: number): void => {
    setActiveIndex((prev: number | null): number | null => (prev === index ? null : index));
  };

  return (
    <div className="keyframe-accordion">
      <div className="keyframe-accordion__header">
        <span className="keyframe-accordion__title">{`Keyframes (${stops.length})`}</span>
        <div className="keyframe-accordion__actions">
          <button
            type="button"
            aria-label="Add keyframe"
            className="keyframe-accordion__icon-button"
            onClick={(e: React.MouseEvent): void => {
              e.stopPropagation();
              addStop();
            }}
            data-testid="effects-keyframe-add-stop"
          >
            <KuikaSvg name="plus" width={14} height={14} />
          </button>
        </div>
      </div>

      <div className="keyframe-accordion__rows">
        {stops.map((stop: ILoopingKeyframeStop, index: number) => {
          const open: boolean = index === activeIndex;
          return (
            <React.Fragment key={index}>
              <div
                className={classNames("keyframe-row", { "keyframe-row--active": open })}
                onClick={(): void => toggle(index)}
                role="button"
                tabIndex={0}
                data-testid={`effects-keyframe-row-${index}`}
              >
                <span className="keyframe-row__offset">{`${stop.offset}%`}</span>
                <span className="keyframe-row__summary">{stopSummary(stop)}</span>
                <button
                  type="button"
                  aria-label="Remove keyframe"
                  className="keyframe-row__trash"
                  disabled={stops.length <= MIN_STOPS}
                  onClick={(e: React.MouseEvent): void => {
                    e.stopPropagation();
                    if (stops.length > MIN_STOPS) removeStopAt(index);
                  }}
                  data-testid={`effects-keyframe-remove-${index}`}
                >
                  <KuikaSvg name="trash" width={13} height={13} />
                </button>
              </div>

              {open && (
                <div
                  className="keyframe-accordion__inline-form"
                  onClick={(e: React.MouseEvent): void => e.stopPropagation()}
                >
                  <div className="effects-animation__row">
                    <KuikaSmallText style={{ color: "var(--dark-2)" }}>Offset</KuikaSmallText>
                    <KuikaSlider
                      value={stop.offset}
                      onChange={(next: number): void =>
                        patchStopAt(index, { offset: Math.min(OFFSET_MAX, Math.max(OFFSET_MIN, next)) })
                      }
                      min={OFFSET_MIN}
                      max={OFFSET_MAX}
                      step={1}
                      precision={0}
                      unit="%"
                      size="sm"
                      dataTestId={`effects-keyframe-offset-${index}`}
                    />
                  </div>

                  <div className="effects-animation__row">
                    <KuikaSmallText style={{ color: "var(--dark-2)" }}>Opacity</KuikaSmallText>
                    <KuikaSlider
                      value={stop.opacity ?? 1}
                      onChange={(next: number): void => patchStopAt(index, { opacity: Math.min(1, Math.max(0, next)) })}
                      min={0}
                      max={1}
                      step={0.01}
                      precision={2}
                      size="sm"
                      dataTestId={`effects-keyframe-opacity-${index}`}
                    />
                  </div>

                  <TransformAccordion
                    items={stop.transforms ?? []}
                    onChange={(next: ITransformItem[]): void => patchStopAt(index, { transforms: next })}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default KeyframeStopEditor;
